import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢订阅！');
      return;
    }

    const formData = new FormData(form);
    try {
      const response = await fetch('https://readdy.ai/api/form/d9spvip4ne17ei0sdlvg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢订阅！您已成功加入我们的 Newsletter。');
        setEmail('');
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (typeof msg === 'string' && (msg.includes('spam') || msg.includes('form data is spam'))) {
          setFormStatus('error');
          setFormMessage(msg);
        } else {
          setFormStatus('error');
          setFormMessage(msg || '订阅失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试。');
    }
  };

  const footerLinks = [
    {
      title: '分类导航',
      links: [
        { label: '欧美电影', href: '/category/oumei-movies' },
        { label: '欧美剧集', href: '/category/oumei-tv' },
        { label: '欧美音乐', href: '/category/oumei-music' },
        { label: '日本电影', href: '/category/japan-movies' },
      ],
    },
    {
      title: '更多分类',
      links: [
        { label: '日本剧集', href: '/category/japan-tv' },
        { label: '日本音乐', href: '/category/japan-music' },
        { label: '韩国电影', href: '/category/korea-movies' },
        { label: '韩国剧集', href: '/category/korea-tv' },
      ],
    },
    {
      title: '关于平台',
      links: [
        { label: '关于我们', href: '/about' },
        { label: '品牌介绍', href: '/about' },
        { label: '联系方式', href: '/about#contact' },
        { label: '韩国音乐', href: '/category/korea-music' },
      ],
    },
  ];

  return (
    <footer className="bg-background-100 border-t border-background-200/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500">
                <i className="ri-global-line text-background-50 text-lg"></i>
              </div>
              <span className="text-lg font-semibold font-heading text-foreground-900 whitespace-nowrap">
                欧美日韩一区
              </span>
            </Link>
            <p className="text-sm text-foreground-600 leading-relaxed mb-6">
              汇聚全球优质影视、音乐、文化内容的精选聚合平台，为您提供欧美日韩一区最全分类资源导航。
            </p>
            <div className="flex items-center gap-3">
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:text-primary-500 hover:bg-primary-100 transition-colors duration-200" aria-label="微博">
                <i className="ri-weibo-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:text-primary-500 hover:bg-primary-100 transition-colors duration-200" aria-label="微信">
                <i className="ri-wechat-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:text-primary-500 hover:bg-primary-100 transition-colors duration-200" aria-label="Bilibili">
                <i className="ri-bilibili-line"></i>
              </a>
            </div>
          </div>

          {footerLinks.map((group) => (
            <div key={group.title}>
              <h4 className="text-sm font-semibold text-foreground-900 mb-4">{group.title}</h4>
              <ul className="space-y-2.5">
                {group.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-8 border-t border-background-200/60">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex-1 max-w-md">
              <h4 className="text-sm font-semibold text-foreground-900 mb-2">订阅 Newsletter</h4>
              <p className="text-xs text-foreground-500 mb-3">获取最新欧美日韩一区精选内容更新通知</p>
              <form
                onSubmit={handleNewsletterSubmit}
                data-readdy-form
                className="flex flex-col sm:flex-row gap-2"
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute left-[-9999px] opacity-0 h-0 w-0 overflow-hidden pointer-events-none"
                />
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="输入您的邮箱地址"
                  className="flex-1 px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                />
                <button
                  type="submit"
                  className="px-5 py-2.5 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  订阅
                </button>
              </form>
              {formStatus !== 'idle' && (
                <p className={`text-xs mt-2 ${formStatus === 'success' ? 'text-primary-600' : 'text-red-500'}`}>
                  {formMessage}
                </p>
              )}
            </div>
            <p className="text-xs text-foreground-500 whitespace-nowrap">
              &copy; 2026 欧美日韩一区精选内容聚合平台. 保留所有权利.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}